import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, Heart, MapPin, MessageCircle, Check } from "lucide-react";
import { STORE, REAL_PHOTOS, waLink, OFFER } from "@/lib/store";

const VALUES = [
  { icon: Users, title: "Bikes for All Ages", text: "From first pedals for toddlers to serious rides for adults — we have something for everyone." },
  { icon: ShieldCheck, title: "Free Helmet & Pump", text: "Every bike leaves our store with a safety helmet and a bike pump, on the house." },
  { icon: Heart, title: "Honest, Local Service", text: "We're your neighbourhood bike experts in Mangoro — real advice, no pressure." },
];

export default function About() {
  useEffect(() => {
    document.title = "About Us | Knoble Rides Enterprises — Bicycle Store Lagos";
  }, []);

  return (
    <div>
      <section className="bg-[#0B0A09] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <span className="kn-overline text-[#e0b48a]">Our Story</span>
          <h1 className="mt-3 font-display text-4xl font-extrabold text-white sm:text-5xl">About Knoble Rides</h1>
          <p className="mt-4 max-w-2xl text-lg text-zinc-300">{STORE.motto} — and we mean it.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl font-bold text-[#0B0A09]">A bicycle shop built for every rider in Lagos</h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-zinc-600">
              <p>
                Knoble Rides Enterprises is a trusted bicycle store based at MRS Filling Station, Mangoro, along the
                Abeokuta Express Road in Lagos, Nigeria. We stock a wide range of quality bikes — electric, mountain,
                road, BMX and kids bicycles — plus all the accessories you need to ride safely.
              </p>
              <p>
                Whether you're buying a child's very first bike or upgrading to a serious machine, our team helps you
                find the right ride at a fair price. We believe cycling should be for everyone, at every age.
              </p>
              <p className="font-semibold text-[#0B0A09]">
                And because your safety matters to us, every bike purchase includes a <span className="text-[#8C5A32]">free helmet and bike pump</span>.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={waLink(`Hello ${STORE.name}, I'd like to know more about your bikes.`)} target="_blank" rel="noopener noreferrer" className="btn-wa inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold">
                <MessageCircle className="h-5 w-5" /> Chat With Us
              </a>
              <Link to="/bikes" className="btn-brown inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold">
                Browse Bikes
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {REAL_PHOTOS.slice(0, 4).map((img, i) => (
              <div key={i} className={`overflow-hidden rounded-2xl border border-[#E6D7C8] bg-[#F1ECE6] ${i % 3 === 0 ? "row-span-1" : ""}`}>
                <img src={img} alt={`Knoble Rides store and bikes Lagos ${i + 1}`} loading="lazy" className="aspect-square w-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <div key={i} className="rounded-2xl border border-[#E6D7C8] bg-[#FDF8F3] p-7">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#8C5A32]">
                  <v.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-[#0B0A09]">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FDF8F3] py-16">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <ShieldCheck className="mx-auto h-12 w-12 text-[#8C5A32]" />
          <h2 className="mt-4 font-display text-3xl font-bold text-[#0B0A09]">The Knoble Promise</h2>
          <p className="mt-3 text-base text-zinc-600">{OFFER} — on every bike, no exceptions.</p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {["Quality bikes for all ages", "Free helmet & pump", "Fast WhatsApp response"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#0B0A09] ring-1 ring-[#E6D7C8]">
                <Check className="h-4 w-4 text-[#8C5A32]" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
